import { makeAutoObservable, runInAction } from "mobx";
import AlertsAPI from "../../api/AlertsAPI";
import {
  ALERT_BULK_MAX_IDS,
  ALERT_VIEW_PARAMS,
  AlertItem,
  AlertListParams,
  AlertSeverity,
  AlertView,
} from "../../../../models/Alerts/Alert";

const compareAlerts = (a: AlertItem, b: AlertItem): number => {
  const byTime =
    new Date(b.last_occurred_at).getTime() -
    new Date(a.last_occurred_at).getTime();
  return byTime !== 0 ? byTime : b.id - a.id;
};

/**
 * One filtered, paginated alert list. The navbar panel and the inbox page
 * each own one, so a cursor change can be merged into whichever is loaded.
 *
 * Lists are grouped: one row per alert group (its newest alert) plus
 * ungrouped alerts. With `groupId` set, the list shows that group's alerts.
 */
export class AlertListModel {
  private api: AlertsAPI;
  private requestSeq = 0;

  view: AlertView;
  section: string | null = null;
  severity: AlertSeverity | null = null;
  /** Group being viewed; `null` = the grouped list. */
  groupId: number | null = null;
  limit: number;

  items: AlertItem[] = [];
  pageNo = 1;
  totalPages = 0;
  totalCount = 0;
  isLoading = false;
  hasLoaded = false;
  error = "";
  actionError = "";
  actionSuccess = "";
  selectedIds: number[] = [];

  constructor(api: AlertsAPI, view: AlertView, limit: number) {
    this.api = api;
    this.view = view;
    this.limit = limit;
    makeAutoObservable<AlertListModel, "api" | "requestSeq">(this, {
      api: false,
      requestSeq: false,
    });
  }

  get params(): AlertListParams {
    return {
      ...ALERT_VIEW_PARAMS[this.view],
      section: this.section ?? undefined,
      severity: this.severity ?? undefined,
      ...(this.groupId
        ? { group_id: this.groupId }
        : { grouped: "true" as const }),
      page_no: this.pageNo,
      limit: this.limit,
    };
  }

  get selectedItems(): AlertItem[] {
    return this.items.filter((item) => this.selectedIds.includes(item.id));
  }

  get isAllSelected(): boolean {
    return (
      this.items.length > 0 &&
      this.items.every((item) => this.selectedIds.includes(item.id))
    );
  }

  /** Sets filters from the URL and loads page 1. */
  init(view: AlertView, section: string | null, groupId: number | null = null) {
    this.view = view;
    this.section = section;
    this.groupId = groupId;
    this.pageNo = 1;
    this.selectedIds = [];
    this.actionError = "";
    this.actionSuccess = "";
    this.load();
  }

  setFilters(filters: { view?: AlertView; severity?: AlertSeverity | null }) {
    const view = filters.view ?? this.view;
    const severity =
      filters.severity === undefined ? this.severity : filters.severity;
    if (view === this.view && severity === this.severity) return;
    this.view = view;
    this.severity = severity;
    this.pageNo = 1;
    this.load();
  }

  setView(view: AlertView) {
    if (this.view === view) return;
    this.view = view;
    this.pageNo = 1;
    this.load();
  }

  setSection(section: string | null) {
    if (this.section === section) return;
    this.section = section;
    this.pageNo = 1;
    this.load();
  }

  setSeverity(severity: AlertSeverity | null) {
    if (this.severity === severity) return;
    this.severity = severity;
    this.pageNo = 1;
    this.load();
  }

  setPage(pageNo: number) {
    if (this.pageNo === pageNo) return;
    this.pageNo = pageNo;
    this.load();
  }

  setActionFeedback(error: string, success = "") {
    this.actionError = error;
    this.actionSuccess = success;
  }

  toggleSelected(id: number) {
    this.selectedIds = this.selectedIds.includes(id)
      ? this.selectedIds.filter((selected) => selected !== id)
      : [...this.selectedIds, id].slice(0, ALERT_BULK_MAX_IDS);
  }

  toggleSelectAll() {
    this.selectedIds = this.isAllSelected
      ? []
      : this.items.map((item) => item.id).slice(0, ALERT_BULK_MAX_IDS);
  }

  clearSelection() {
    this.selectedIds = [];
  }

  async load() {
    const seq = ++this.requestSeq;
    this.isLoading = true;
    this.error = "";
    try {
      const result = await this.api.listAlerts(this.params);
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.items = result.list;
        this.totalPages = result.total_pages;
        this.totalCount = result.total_count;
        this.hasLoaded = true;
        this.selectedIds = this.selectedIds.filter((id) =>
          result.list.some((item) => item.id === id),
        );
      });
    } catch (error: any) {
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.error = error?.message || "Failed to load alerts.";
      });
    } finally {
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  /** Whether a row belongs in this list under the current filters. */
  matches(item: AlertItem): boolean {
    const { status, read, archived } = ALERT_VIEW_PARAMS[this.view];
    if (this.groupId && item.group?.id !== this.groupId) return false;
    if (this.section && item.section !== this.section) return false;
    if (this.severity && item.severity !== this.severity) return false;

    const isArchived = item.me.archived_at != null;
    if (archived === "false" && isArchived) return false;
    if (archived === "true" && !isArchived) return false;

    if (read === "UNREAD" && item.me.read_at != null) return false;
    if (read === "READ" && item.me.read_at == null) return false;

    if (status === "ACTIONABLE") {
      return item.status === "OPEN" || item.status === "NONE";
    }
    if (status === "OPEN" || status === "RESOLVED") {
      return item.status === status;
    }
    return true;
  }

  /**
   * Upserts changed rows; rows that no longer match the filter drop out.
   *
   * @param rows - Changed rows.
   * @param fromServer - `true` for rows from the change feed. A grouped list
   *   reloads when any of them is grouped: which alert leads a group, and the
   *   group's size, can only come from the server.
   */
  merge(rows: AlertItem[], fromServer = false) {
    if (!this.hasLoaded || rows.length === 0) return;
    if (fromServer && !this.groupId && rows.some((row) => row.group)) {
      this.load();
      return;
    }
    let items = [...this.items];
    let countDelta = 0;

    rows.forEach((row) => {
      const index = items.findIndex((item) => item.id === row.id);
      const belongs = this.matches(row);
      if (index >= 0 && belongs) {
        items[index] = row;
      } else if (index >= 0) {
        items.splice(index, 1);
        countDelta -= 1;
      } else if (belongs && this.pageNo === 1 && (this.groupId || !row.group)) {
        // A grouped list never adds a group member locally (see above).
        items.push(row);
        countDelta += 1;
      }
    });

    items.sort(compareAlerts);
    if (this.pageNo === 1 && items.length > this.limit) {
      items = items.slice(0, this.limit);
    }
    this.items = items;
    this.totalCount = Math.max(0, this.totalCount + countDelta);
    this.selectedIds = this.selectedIds.filter((id) =>
      items.some((item) => item.id === id),
    );
  }

  findItem(id: number): AlertItem | undefined {
    return this.items.find((item) => item.id === id);
  }
}
